import BaseStore from './base-store'
import StoreProxy from './store_proxy'
import $images, {is_image} from './images.js'
import {Rest} from 'utils'
import {API2_FILES} from 'consts/api2'
import {FTYPES} from 'consts/ftypes'


import * as $utils from 'utils';
export class FileUpload extends BaseStore {
  constructor(file, params, options = {}) {
    const def_options = {
      resolve: undefined,
      reject: undefined,
      endpoint: API2_FILES,
      show_toasts: false,
      ...options
    }
    super({
      id: crypto.randomUUID(),
      file: file,
      params: params,
      error: undefined,
      error_detail: undefined,
      result: undefined,
      bytes_loaded: 0,
      aborted: false,
      ...def_options
    })
  }

  abort() {
    if (this._state.xhr) {
      this._state.xhr.abort()
    }
  }

  get started(){
    return !!this._state.xhr
  }

  get active() {
    if (this._state.error || this._state.result || this._state.aborted) {
      return false
    }
    return true
  }

  async start() {
    this.abort()

    this._state.error = undefined
    this._state.result = undefined
    this._state.bytes_loaded = 0
    this._state.aborted = false
    this._state.xhr = new XMLHttpRequest()

    const xhr = this._state.xhr
    xhr.open('PUT', this.endpoint, true)

    const auth = await this._rest.auth_headers()
    for (const key in auth) {
      xhr.setRequestHeader(key, auth[key])
    }

    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')
    xhr.setRequestHeader('X-Filename', encodeURIComponent(this._state.file.name)) // headers allow only ISO-8859-1 chars
    xhr.setRequestHeader('X-File-Type', this._state.params.type)
    xhr.setRequestHeader('Content-Type', this._state.file.type)

    if (this._state.params.tags) {
      xhr.setRequestHeader('X-File-Tags', this._state.params.tags)
    }

    xhr.upload.addEventListener('abort', () => {
      this._state.aborted = true
    })

    xhr.upload.addEventListener('error', () => {
      this._state.error = new Error(`Error uploading file. Status: ${xhr.status}, response: ${xhr.responseText}'`)
      if (this._state.reject) this._state.reject(this._state.error, this)
    })

    xhr.upload.addEventListener('timeout', () => {
      this._state.error = new Error('File upload timed out')
    })

    xhr.upload.addEventListener('progress', (ev) => {
      this._state.bytes_loaded = ev.loaded
    })

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            this._state.result = JSON.parse(xhr.responseText)
            if (this._state.resolve) this.resolve(this._state.result, this)
          }
          catch (err) {
            this._state.error = err
          }
        }
        else {
          if ($utils.validate.isJsonString(xhr.responseText)) {
            let jsonErr = JSON.parse(xhr.responseText)
            this._state.error_detail = Array.isArray(jsonErr) ? jsonErr.at(0) : jsonErr.detail
          }

          if (!this._state.error_detail) {
            this._state.error_detail = xhr.responseText?.trim() || 'Unknown Error'
          }

          if (this._state.show_toasts) {
            let text = `Failed to upload '${this._state.file.name}'.`
            if (this._state.error_detail) text += ` ${this._state.error_detail}.`
            FileUpload.showError(text)
          }

          this._state.error = new Error(`Error uploading file. Status: ${xhr.status}, response: ${xhr.responseText}`)
          console.log(this._state.error.message)

          if (this._state.reject) this._state.reject(this._state.error, this)
        }
      }
    }

    xhr.send(this._state.file)
  }

  static showError(message) {
    $utils.toasts.error(message)
  }

  static checkAllowed(file, type) {
    const info = FTYPES[type]

    if (file.name.split('.').length > 2) {
      FileUpload.showError(`Cannot upload '${file.name}'. The file extension contains two dots.`)
      return false
    }

    if (!info) {
      FileUpload.showError(`Cannot upload '${file.name}'. Unknown file type.`)
      return false
    }

    if (file.size > info.max_size) {
      const pretty_bytes = $utils.format.bytes(info.max_size)
      FileUpload.showError(`Cannot upload '${file.name}'. File size limit of ${pretty_bytes} exceeded.`)
      return false
    }

    return true
  }
}

export class File extends BaseStore {
  constructor(file, endpoint = API2_FILES) {
    // TODO: GAI-211 sort tags alphabetically, also in doLoad
    //       we do not do it now cause this can take a lot of
    //       time when we have a lot of files until real
    //       pagination is implemented
    super({
      id: undefined,
      user_id: undefined,
      s3_key: undefined,
      name: undefined,
      ext: undefined,
      tags: [],
      type: undefined,
      size: undefined,
      status: undefined,
      weaviate_id: undefined,
      created_at: undefined,
      deleting: false,
      ...file,
      endpoint: endpoint
    })

    if (this.is_image) {
      $images.track(this._state)
    }
  }

  get is_image() {
    return is_image(this._state.type)
  }

  get failed() {
    return this._state.status === 'failed-processing'
  }

  get processing() {
    if (this._state.type === 'document' || this._state.type === 'linkedin-contacts') {
      return this._state.status === 'uploaded' || this._state.status == 'processing'
    }
    return false
  }

  async doLoad(rest, params) {
    const fdata = await rest.get(`${this.endpoint}/${this.id}`)
    $utils.object.proxy_assign(this, fdata)

    if (this.is_image) {
      $images.track(this._state)
    }
    else if (this._state.type === 'linkedin-contacts') {
      if (this.failed && this._state.show_toasts)
        FileUpload.showError('Failed to import LinkedIn contacts')
    }
  }
}

class Files extends BaseStore {
  // File store can 'view' files by type & tag
  constructor(type, tag = undefined, endpoint = API2_FILES) {
    super({
      files: [],
      uploads: [],
      watchers: [],
      filtered: false,
      total: 0,
      count: 0,
      ext: [],
      tags: [],
      endpoint: endpoint
    })

    this.type = type
    this.tag = tag
    this.watch = new $utils.timers.Later(5000)
  }

  get view() {
    const result = {type: this.type}
    if (this.tag) result['tags'] = [this.tag]
    return result
  }

  async doAbort() {
    this._state.files.forEach(file => file.abortAll())
  }

  _watch_processing() {
    this.watch.abort()
    this._state.watchers = []

    this.watch.repeat(async () => {
      this._state.watchers = this._state.files.filter(f => f.processing)

      if (this._state.watchers.length === 0) {
        this.watch.abort()
        return
      }

      const tasks = this._state.watchers.map(file => file.load())
      await Promise.all(tasks)
    })
  }

  async doLoad(rest, params) {
    const {total = true, ...query} = params

    if (total)  {
      const data = await rest.get(`${this.endpoint}/stats`, {...this.view})
      this._state.total = data.total
    }

    // TODO: check, if query have save keys as store view then combine, not override
    const data = await rest.get(this.endpoint, {...query, ...this.view})

    if (!data.count) {
      this._state.files = []
      this._state.count = 0
      this._watch_processing()
      return
    }

    this._state.files = data.files.map(f => new File(f, this.endpoint))
    this._state.count = data.count
    this._watch_processing()
  }

  async doSave(rest, changed) {
  }

  deleteUpload(id) {
    const ind = this._state.uploads.findIndex(el => el.id === id)
    this._state.uploads[ind].abort()

    const uploads = this._state.uploads.filter(el => el.id !== id)
    this._state.uploads = uploads
  }

  async upload(file, params = {keep: false}) {
    if (!FileUpload.checkAllowed(file, this.view.type)) {
      return
    }

    const {resolve, to_files = true} = params
    const upload = new FileUpload(file, this.view, {
      resolve: async (result, upload) => {
        const file = new File(result, this._state.endpoint)

        // if we already have file with the same id (reupload) then replace
        // otherwise just add new file
        const curr_idx = this._state.files.findIndex(file => file.id === result.id)
        if (curr_idx !== -1) {
          this._state.files[curr_idx] = file
        }
        else if (to_files) {
          this._state.files.push(file)
          this._state.total += 1
        }

        if (is_image(this.type)) {
          $images.track(result)
        }

        // remove finished upload
        if (!params.keep) {
          const uploads = this._state.uploads.filter(el => el.id !== upload.id)
          this._state.uploads = uploads
        }

        // watch processing
        this._watch_processing()

        if (resolve) {
          resolve(result, upload)
        }

      }})

    this._state.uploads.push(upload)
    await upload.start()
  }


  async uploadMany(files, params = {}) {
    const MAX_CONCURRENT = 5
    const {resolve} = params

    const nextStep = () => {
      const next_upload = this._state.uploads.find(x => !x.started)
      if (!next_upload) {
        if (resolve && uploads.every(x => !x.active)) resolve()
        return
      }
      next_upload.start()
    }

    const uploads = files.map(file => new FileUpload(file, this.view,{
      endpoint: this._state.endpoint,
      resolve: (result) => {
      // if we already have file with the same id (reupload) then replace
        const curr_idx = this._state.files.findIndex(file => file.id === result.id)
        if (curr_idx !== -1) {
          const file = new File(result, this._state.endpoint)
          this._state.files[curr_idx] = file
          this._watch_processing()
        }
        // new Later(1000).once(nextStep)
        nextStep()
      },
      //reject: () => new Later(1000).once(nextStep)
      reject: nextStep
    }))

    this._state.uploads.push(...uploads)


    // check for active uploads
    const active_count = $utils.array.hasAtLeast(
      this._state.uploads, x => x.started && x.active, MAX_CONCURRENT
    )
    if (active_count >= MAX_CONCURRENT) return

    for (let i = 0; i < Math.min(MAX_CONCURRENT - active_count, uploads.length); i++) {
      const upload = uploads[i]
      upload.start()
    }
  }

  async delete(file_id) {
    let idx = this._state.files.findIndex(file => file.id === file_id)
    this._state.files[idx].deleting = true

    await this._rest.delete(`${this.endpoint}/${file_id}`)
    this.remove(file_id)
  }

  remove(file_id) {
    const idx = this._state.files.findIndex(file => file.id === file_id)
    const file = this._state.files.splice(idx, 1)[0]

    if (file.is_image) {
      $images.untrack(file_id)
    }

    this._watch_processing()
  }

  async addSamplePost(inline_text) {
    //
    // Form file name
    //

    // remove all non-eni codepoints and commas
    let text = inline_text.replace(/[^\x20-\x7F]/g, '').replace(',', '').trim()
    if (text.length === 0) text = 'Sample post'

    // Get first 3 words
    let idx = $utils.str.nthIndexOf(text, ' ', 3)
    idx = idx == -1 ? Math.min(text.length, 15) : idx
    const name = text.substring(0, idx)

    // If same file name already exists add counter and try again
    const findFile = (check) => {
      for (const file of this._state.files) {
        if (file.name === check) {
          return true
        }
      }
      return false
    }

    let counter = 0
    let fname = `${name}.txt`
    while(findFile(fname)) {
      counter++
      fname = `${name}_${counter}.txt`
    }

    //
    // Create and upload file
    //
    const file = new window.File([inline_text], fname, {type: 'text/plain'})
    return new Promise((resolve) => {
      this.upload(file, {resolve: (result) => resolve(result)})
    })
  }
}

//
// Just get file meta from server
// File object won't belong to any store
//
export async function get_file(file_id, endpoint = API2_FILES) {
  const rest = new Rest()
  const json = await rest.get(`${endpoint}/${file_id}`)
  return new File(json)
}

export async function downloadFile(file_id, endpoint = API2_FILES) {
  const file = await get_file(file_id, endpoint)
  await $utils.downloadFile(file.url)
}

//
// Global stores
//
const stores = {}

export function create_store(type, tag = undefined, endpoint = API2_FILES) {
  return new StoreProxy(new Files(type, tag, endpoint), {array_prop: 'files'})
}

export function get_store(type, tag = undefined, endpoint = undefined) {
  let key = type
  if (tag) key = key + `-${tag}`
  else if (endpoint) key = key + `-${endpoint}`

  if (!stores[key]) stores[key] = create_store(type, tag, endpoint)
  return stores[key]
}

const documents = get_store('document')
export default documents
